import { useCurrentCloudProvider } from '@labrav/common'
import React, { useMemo } from 'react'
import { HelpDrawerWithAccordion } from '@labrav/common'
import { landingPageImages } from '../../../common/utils/productCreationUtil'
import {
  FaqQuestion,
  FlyoutProductCreationFaqSection,
  HelpDrawerFaqItems,
} from '../FlyoutProductFormHelpFaqs/ProductCreationFaqUtil'
import { CarouselWithModal } from '../../../common/components/CarouselWithModal/CarouselWithModal'
import { CloudType } from '../../../types/index'

const awsFaqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="What is a Buyer Registration Page?"
      />
    ),
    answer: (
      <div>
        This is the page where customers are redirected after purchasing your
        product on AWS Marketplace. When they click{' '}
        <strong>"Set up my account"</strong>, they land here to complete their
        registration. This step ensures that you collect essential buyer details
        before they gain access to your product.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Why do I need this page?"
      />
    ),
    answer: (
      <div>
        AWS only provides a unique AWS account ID upon subscription—it does not
        share any buyer contact details. Without this page, you won’t know who
        your buyer is. This form helps you capture key buyer details like their
        name, company, and email. Labra then sends this information to you,
        enabling a smooth onboarding experience for your customers.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="What happens if I don't use Labra's hosted Buyer Registration Page?"
      />
    ),
    answer: (
      <div>
        <p>
          If you choose to{' '}
          <strong>host the Buyer Registration Page yourself</strong>, Labra will{' '}
          <strong>not</strong> be able to capture buyer details or send
          automated notifications to your buyers. As a result
        </p>
        <ol style={{ paddingLeft: '20px' }}>
          <li>
            Buyers will appear as “Unknown” in Labra’s notifications and
            reports.
          </li>
          <li>
            You won’t receive important buyer details within Labra, making it
            harder to track contracts, renewals, and customer interactions.
          </li>
          <li>
            <strong>
              You will need to manually handle buyer communications
            </strong>
            , including onboarding instructions and other important
            notifications that Labra would otherwise send automatically.
          </li>
        </ol>
        <p>
          However, if you have an internal system that{' '}
          <strong>
            captures buyer information and manages buyer notifications
          </strong>
          , you can choose to self-host this page while ensuring buyers receive
          the necessary updates.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="What fields must I fill to preview the Buyer Registration Page?"
      />
    ),
    answer: (
      <div>
        To see a preview of your custom Buyer Registration Page, you need to
        complete the <strong>Branding and Configuration</strong> sections,
        ensuring all mandatory fields are filled.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="What happens after a buyer submits the Labra-hosted registration form?"
      />
    ),
    answer: (
      <div>
        <p>The next steps depend on whether the buyer is new or existing:</p>
        <ol style={{ paddingLeft: '20px' }}>
          <li>
            <strong>New buyers:</strong> After submitting the form, they are
            automatically redirected within 5 seconds to the{' '}
            <strong>Application Login Page URL</strong> you provide.
          </li>
          <li>
            <strong>Existing buyers:</strong> if the buyer has already
            registered before, they skip the form and go straight to the login
            page.
          </li>
        </ol>
        <p>
          This ensures that your customers can start using your product without
          unnecessary delays.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which fields are mandatory to preview the buyer registration page?"
      />
    ),
    answer: (
      <div>
        You need to fill the mandatory fields in Buyer registration page
        branding section and the Heading message field inside Buyer registration
        page configuration.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Can I customize the Buyer registration page according to my needs?"
      />
    ),
    answer: (
      <div>
        Yes, you can customize all components of the buyer registration page as
        per your requirement and branding.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="How does the Buyer registration page look like?"
      />
    ),
    answer: (
      <div>
        The Buyer registration page will look similar to the picture added at
        the top of the page. Please note - that image is a demo, and your page
        will look as per the options chosen by you.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which background color am I choosing?"
      />
    ),
    answer: (
      <div>
        The background color chosen will be reflected in the background of the
        image as shown in the demo picture.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which Button color am I choosing?"
      />
    ),
    answer: (
      <div>
        You will choose the color of the ‘Submit’ button as shown in the demo
        picture.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which Button text color am I choosing?"
      />
    ),
    answer: (
      <div>
        You will choose the color of the button text ‘Submit’ as shown in the
        demo picture.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Where will the background image that I upload be visible?"
      />
    ),
    answer: (
      <div>
        The image uploaded by you will be shown on the left side of the buyer
        registration page as shown in the demo picture.
      </div>
    ),
  },
]

const azureFaqItems: HelpDrawerFaqItems[] = [
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="What is a Buyer Registration Page?"
      />
    ),
    answer: (
      <div>
        This is the page where customers are redirected after purchasing your
        product on Microsoft Marketplace. This step ensures that you collect
        essential buyer details before they gain access to your product.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Why do I need this page?"
      />
    ),
    answer: (
      <div>
        This form helps you capture key buyer details like their name, company,
        and email. Platform sends this information to you. The contract is
        created after the buyer fills this page.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="What fields must I fill to preview the Buyer Registration Page?"
      />
    ),
    answer: (
      <div>
        To see a preview of your custom Buyer Registration Page, you need to
        complete the <strong>Branding and Configuration</strong> sections,
        ensuring all mandatory fields are filled.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="What happens after a buyer submits the Labra-hosted registration form?"
      />
    ),
    answer: (
      <div>
        <p>The next steps depend on whether the buyer is new or existing:</p>
        <ol style={{ paddingLeft: '20px' }}>
          <li>
            <strong>New buyers:</strong> After submitting the form, they are
            automatically redirected within 5 seconds to the{' '}
            <strong>Application Login Page URL</strong> you provide.
          </li>
          <li>
            <strong>Existing buyers:</strong> if the buyer has already
            registered before, they skip the form and go straight to the login
            page.
          </li>
        </ol>
        <p>
          This ensures that your customers can start using your product without
          unnecessary delays.
        </p>
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which fields are mandatory to preview the buyer registration page?"
      />
    ),
    answer: (
      <div>
        You need to fill the mandatory fields in Buyer registration page
        branding section and the Heading message field inside Buyer registration
        page configuration.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Can I customize the Buyer registration page according to my needs?"
      />
    ),
    answer: (
      <div>
        Yes, you can customize all components of the buyer registration page as
        per your requirement and branding.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="How does the Buyer registration page look like?"
      />
    ),
    answer: (
      <div>
        The Buyer registration page will look similar to the picture added at
        the top of the page. Please note - that image is a demo, and your page
        will look as per the options chosen by you.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which background color am I choosing?"
      />
    ),
    answer: (
      <div>
        The background color chosen will be reflected in the background of the
        image as shown in the demo picture.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which Button color am I choosing?"
      />
    ),
    answer: (
      <div>
        You will choose the color of the ‘Submit’ button as shown in the demo
        picture.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Which Button text color am I choosing?"
      />
    ),
    answer: (
      <div>
        You will choose the color of the button text ‘Submit’ as shown in the
        demo picture.
      </div>
    ),
  },
  {
    question: (
      <FaqQuestion
        section={FlyoutProductCreationFaqSection.BUYER_REGISTRATION_BRANDING}
        question="Where will the background image that I upload be visible?"
      />
    ),
    answer: (
      <div>
        The image uploaded by you will be shown on the left side of the buyer
        registration page as shown in the demo picture.
      </div>
    ),
  },
]

interface FlyoutProductBrandingFaqsProps {
  indexModal: number
  setIndexModal: (index: number) => void
}

export const FlyoutProductBrandingFaqs: React.FC<
  FlyoutProductBrandingFaqsProps
> = ({ indexModal, setIndexModal }) => {
  const selectedCloud = useCurrentCloudProvider()
  const faqItems = useMemo(() => {
    return selectedCloud === CloudType.AWS ? awsFaqItems : azureFaqItems
  }, [selectedCloud])
  return (
    <HelpDrawerWithAccordion
      headerTitle="Help & FAQs - Branding"
      faqItems={faqItems}
      customContent={
        <CarouselWithModal
          carouselData={landingPageImages.slice(0, 1)}
          setCurrentIndex={setIndexModal}
          currentIndex={indexModal}
        />
      }
    />
  )
}
